import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { userApi } from "../apis/user/api";
import { SessionData } from "../types/session";
import LoadingSpinner from "../components/common/LoadingSpinner/LoadingSpinner";
import { SessionManager } from "../services/storage/sessionManager";

/* global console */

interface UserSessionContextType {
  isLoggedIn: boolean;
  sessionData: SessionData | null;
  setSessionData: (data: SessionData | null) => void;
  logout: () => void;
}

const defaultSessionContext: UserSessionContextType = {
  isLoggedIn: false,
  sessionData: null,
  setSessionData: () => undefined,
  logout: () => undefined,
};

const UserSessionContext = createContext<UserSessionContextType | null>(
  defaultSessionContext,
);

const UserSessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const sessionManager = SessionManager.getInstance();
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initSession = async () => {
      try {
        const initialSession = await sessionManager.getSession();
        setSessionData(initialSession);
      } catch (error) {
        console.error("Error initializing session:", error);
      } finally {
        setIsInitialized(true);
      }
    };

    initSession();
  }, []);

  const isLoggedIn = useMemo(() => !!sessionData?.isSuccess, [sessionData]);

  const logout = async () => {
    try {
      await userApi.signOut();
    } catch (err) {
      console.log(err);
    } finally {
      setSessionData(null);
      await sessionManager.clearSession();
    }
  };

  const handleSetSessionData = async (data: SessionData | null) => {
    setSessionData(data);
    if (data) {
      await sessionManager.saveSession(data);
    }
  };

  const contextValue = useMemo(
    () => ({
      isLoggedIn,
      sessionData,
      setSessionData: handleSetSessionData,
      logout,
    }),
    [isLoggedIn, sessionData],
  );

  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  return (
    <UserSessionContext.Provider value={contextValue}>
      {children}
    </UserSessionContext.Provider>
  );
};

export const useUserSessionContext = () => {
  const context = useContext(UserSessionContext);

  if (!context) {
    throw new Error("useUserSession must be used within a UserSessionProvider");
  }
  return context;
};

export default UserSessionProvider;
